// `pointerdown` covers mouse, pen and touch, so listening for `mousedown` and
// `touchstart` as well would only probe the same gesture several times over.
const UNLOCK_EVENTS = ['keydown', 'pointerdown']

/**
 * Keeps notification/ringtone audio elements playable under browser autoplay
 * policies.
 *
 * On the new engine an inbound call is announced through the TaskRouter Worker
 * SDK and the ringtone is an HTMLAudioElement we play ourselves, so it is
 * subject to the autoplay policy — unlike the old engine, where the ring comes
 * out of the Twilio Voice SDK. A tab that was loaded but never interacted with
 * therefore rang silently and the agent missed the call.
 *
 * Two things lift the restriction, and this service uses both:
 *   1. any real user gesture on the page,
 *   2. a microphone permission grant on the origin (Chromium allows autoplay
 *      with sound there) — the dialer requests the mic, so an agent who ever
 *      allowed it stays unlocked across reloads without touching the page.
 *
 * Registered elements are probed at volume 0 (not `muted` — muted playback is
 * always allowed and would tell us nothing) so an unlock can be confirmed and
 * the element pre-activated without the agent hearing anything.
 */
class AudioPlaybackService {
  constructor () {
    this._elements = new Set()
    this._played = new Set()
    this._pending = new Set()
    this._probing = new Map()
    this._microphoneStatus = null
    this._lastErrorName = null
    this._unlocked = false
    this._listening = false
    this._onInteraction = () => {
      this.unlock()
    }
  }

  // Name of the last playback failure, so callers can log why a sound was not
  // audible instead of assuming an autoplay refusal.
  get lastErrorName () {
    return this._lastErrorName
  }

  get unlocked () {
    // An element that has reached playback proves audio works on this page, and
    // that fact cannot be undone by a later refusal — otherwise one blocked ring
    // would leave the diagnostics reporting `unlocked: false` forever, since
    // nothing recomputes it once the unlock listeners have detached.
    return this._unlocked || this._played.size > 0
  }

  register (element) {
    if (!element) {
      return
    }

    this._elements.add(element)

    if (this._listening) {
      this._probe(element)
    }
  }

  /**
   * Begin watching for anything that can lift the autoplay restriction, and
   * probe right away in case the origin is already unlocked (mic grant kept
   * from a previous session).
   */
  start () {
    if (this._listening) {
      return
    }

    this._listening = true

    UNLOCK_EVENTS.forEach(event => {
      window.addEventListener(event, this._onInteraction, { passive: true })
    })

    this._watchMicrophonePermission()
    this.unlock()
  }

  stop () {
    this._listening = false

    UNLOCK_EVENTS.forEach(event => {
      window.removeEventListener(event, this._onInteraction)
    })
  }

  /**
   * Drop tracked elements. The store rebuilds the audio elements on every layout
   * mount (login, account switch), so the previous ones must not be kept alive
   * here.
   */
  reset () {
    this.stop()
    this._clearMicrophoneWatch()

    // Drop the bookkeeping before touching any element: assignments on a media
    // element can throw, and a throw here escapes into MainLayout's beforeDestroy,
    // which would otherwise leave every set populated with dead elements for the
    // next mount to probe.
    const probing = Array.from(this._probing.entries())

    this._elements.clear()
    this._played.clear()
    this._pending.clear()
    this._probing.clear()
    this._unlocked = false

    // Hand back any element still mid-probe, or it would be stranded at volume 0
    // with looping off — a permanently silent ringtone if the same element is ever
    // registered again. Pause first: the probe's play() can still be pending, and
    // restoring alone would leave a dropped element looping the ringtone at full
    // volume with nothing left holding a reference to stop it. Never clips a live
    // ring — play() takes an element out of `_probing` before playback starts.
    probing.forEach(([element, original]) => {
      element.pause()
      element.currentTime = 0
      element.volume = original.volume
      element.loop = original.loop
    })
  }

  /**
   * Probe every registered element. Resolves to whether audio is playable.
   */
  unlock () {
    const probes = Array.from(this._elements).map(element => this._probe(element))

    return Promise.all(probes).then(results => {
      this._unlocked = results.some(played => played)

      // Activation lasts for the lifetime of the page, so once audio is known to
      // be playable there is nothing left to watch for. Detaching also stops
      // every later keystroke and click from driving another play/pause cycle on
      // the shared ringtone elements — and closes the window in which a probe
      // could collide with a real ring.
      if (this._unlocked) {
        this.stop()
      }

      return this._unlocked
    })
  }

  /**
   * Play an element. Never throws: resolves to false when the browser refused,
   * so the caller can fall back to a notification the agent can actually
   * perceive.
   */
  play (element) {
    if (!element) {
      return Promise.resolve(false)
    }

    // Both done synchronously, before playback starts: `_pending` stops a later
    // gesture from probing the ring we are starting, and the restore undoes an
    // in-flight probe's `volume = 0` so the ring is audible from its first frame
    // rather than from whenever that probe resolves.
    this._pending.add(element)
    this._restoreProbe(element)

    return Promise.resolve()
      .then(() => {
        // Every call rings from the top: only the probe used to reset this, so a
        // ring cut short left the next one starting part-way into the tone. Kept
        // inside the chain so a throwing assignment surfaces as a resolved `false`
        // rather than escaping to callers that have no catch.
        if (element.paused) {
          element.currentTime = 0
        }

        return element.play()
      })
      .then(() => {
        this._pending.delete(element)
        this._played.add(element)
        this._lastErrorName = null
        this._unlocked = true
        return true
      })
      .catch(err => {
        this._pending.delete(element)
        this._lastErrorName = err?.name || 'Error'

        // An AbortError means something paused the element while this play() was
        // still pending — the sound was cancelled on purpose, not refused by the
        // browser, so it says nothing about whether audio is playable. Anything
        // else may be an autoplay refusal (NotAllowedError) or an unplayable
        // source (NotSupportedError — a 404 or unreachable API), so name it rather
        // than asserting a cause.
        if (this._lastErrorName !== 'AbortError') {
          this._unlocked = false
          console.warn(`AudioPlaybackService: playback failed (${this._lastErrorName})`, err)
        }

        return false
      })
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  _probe (element) {
    if (!element) {
      return Promise.resolve(false)
    }

    // Never probe an element we have already asked to play: it is either ringing
    // right now — pausing it would kill a live ring, and a gesture on the Accept
    // button would land here mid-ring — or it has played before, which already
    // activated it for the lifetime of the page.
    // `_pending` and a non-paused element only mean we asked to play, which is not
    // yet proof that the browser allowed it — `_played` is the signal that proves
    // something, so report that rather than assuming success.
    if (!element.paused ||
      this._pending.has(element) ||
      this._played.has(element) ||
      // A probe already owns this element's volume/loop. Re-entering would save
      // the muted values as the originals, and every restore afterwards — in
      // play(), in this continuation, in reset() — would hand back silence.
      this._probing.has(element)) {
      return Promise.resolve(this._played.has(element))
    }

    // Probe inaudibly at volume 0 rather than with `muted`, which the autoplay
    // policy always permits and so would prove nothing.
    this._probing.set(element, { volume: element.volume, loop: element.loop })
    element.volume = 0
    element.loop = false

    return Promise.resolve()
      .then(() => element.play())
      .then(() => {
        // reset() may have dropped this element while the probe was in flight.
        // Recording it as playable would then describe an element nobody uses, and
        // the freshly registered ones could all be refused while the diagnostics
        // still claimed the page was unlocked.
        if (!this._elements.has(element)) {
          // Pause as well: playback began, and for a dropped element nothing else
          // is left to stop it.
          element.pause()
          element.currentTime = 0
          this._restoreProbe(element)
          return false
        }

        // A real ring can start while this probe is still resolving: `play()`
        // flips `paused` synchronously, but its promise only settles once playback
        // actually begins — on a cold tab, after the mp3 has downloaded. `play()`
        // has already restored the settings in that case, so leave the element
        // alone; pausing it here would kill the ring while the ring's own `play()`
        // still reported success.
        const isLive = this._pending.has(element) || this._played.has(element)

        // Pause before restoring: this promise settles only once playback has
        // actually begun, so raising the volume first would leak a fragment of the
        // ringtone — reachable by probing from the agent's click on Accept, where a
        // gesture makes an otherwise-refused probe succeed.
        if (!isLive) {
          element.pause()
          element.currentTime = 0
        }

        this._restoreProbe(element)

        // Reaching playback activated the element for the lifetime of the page,
        // so later gestures never need to probe it again.
        this._played.add(element)
        this._unlocked = true
        return true
      })
      .catch(() => {
        // Deliberately does not record `_lastErrorName`: a refused probe is an
        // expected outcome and must not overwrite the reason a real ring failed.
        this._restoreProbe(element)
        return false
      })
  }

  _clearMicrophoneWatch () {
    if (!this._microphoneStatus) {
      return
    }

    this._microphoneStatus.onchange = null
    this._microphoneStatus = null
  }

  // Undo a probe's volume/loop override. Idempotent, and a no-op for an element
  // that is not being probed, so both play() and the probe itself can call it.
  _restoreProbe (element) {
    const original = this._probing.get(element)

    if (!original) {
      return
    }

    this._probing.delete(element)
    element.volume = original.volume
    element.loop = original.loop
  }

  _watchMicrophonePermission () {
    if (!navigator.permissions || !navigator.permissions.query) {
      return
    }

    navigator.permissions.query({ name: 'microphone' })
      .then(status => {
        // The query can resolve after reset() — installing a handler then would
        // outlive the layout that asked for it.
        if (!this._listening) {
          return
        }

        // Detach the previous cycle's handler, so repeated start()/reset() cycles
        // don't leave a live listener on every PermissionStatus ever queried.
        this._clearMicrophoneWatch()
        this._microphoneStatus = status

        if (status.state === 'granted') {
          this.unlock()
        }

        status.onchange = () => {
          if (status.state === 'granted') {
            this.unlock()
          }
        }
      })
      .catch(() => {
        // Permissions API unsupported for microphone — gestures remain the only
        // unlock path.
      })
  }
}

export default new AudioPlaybackService()
